import { Skeleton, Typography } from 'antd';
import { push } from 'connected-react-router';
import { useGetPendingAssessmentData } from 'lib/hooks/tanstack/use-getPendingAssessmentData';
import { RootState } from 'lib/store/rootReducer';
import { setFulfilled, setRequired } from 'navigation/menu/rx-progress-slice';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { PatientRoutes } from 'routes';
import { CrxMobileDeviceAssessmentBlocked } from '../assessment-intro';
import { useControlFlowState } from './_utils/use-controlFlowState';
import { resolveNeededComponent } from './_utils/config-questionnaire-flow';
import { ErrorDisplay } from 'components/ErrorDisplay';

/**
 * Responsible for routing the user to the next assessment they need to complete
 *
 * Cases
 * 1. The user is not currently completing a pending assessment. Redirect them to the root
 * 2. The user is on mobile. Block the page
 * 3. The user completed all the questionnaires in a pending assessment. Redirect them back to the root
 * 4. The user has been redirected here from another assessment. Promis had some special case that needed to be handled
 * 5. The user navigated here manually. Resolve which questionnaires they need to complete
 */
export function QuestionnaireControlFlow() {
  const user = useSelector((state: RootState) => state.auth.user);
  if (!user) return <Skeleton />;
  return <QuestionnaireControlFlowWithUser patientId={user.uid} />;
}

function QuestionnaireControlFlowWithUser(props: { patientId: string }) {
  /**
   * Use `questionnaireData` for the latest data from the database
   * TODO: Remove `questionnaireControlState`
   */
  const currentAssessmentData = useGetPendingAssessmentData(props.patientId);
  const questionnaireControlState = useControlFlowState(props.patientId);

  const reduxDispatch = useDispatch();
  const locationState = useLocation<
    { fromQuestionnaire: boolean; questionnaireName: string } | undefined
  >();

  /**
   * Update the progress bar
   */
  useEffect(() => {
    reduxDispatch(setFulfilled(questionnaireControlState.fulfilled));
    reduxDispatch(setRequired(questionnaireControlState.required));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireControlState.fulfilled, questionnaireControlState.required]);

  /**
   * Redirect the user to the correct page when the loading is complete
   */
  useEffect(() => {
    if (
      questionnaireControlState.allDone &&
      !questionnaireControlState.isLoading
    ) {
      reduxDispatch(push('/'));
    }
  }, [
    questionnaireControlState.allDone,
    questionnaireControlState.isLoading,
    reduxDispatch
  ]);

  /**
   * Show an error message that assessments cannot be completed on mobile devices
   *
   * TODO: Block this at a higher route
   */
  if (isMobile) {
    return <CrxMobileDeviceAssessmentBlocked />;
  }

  if (
    !props.patientId ||
    questionnaireControlState.allDone ||
    !locationState ||
    currentAssessmentData.isLoading
  ) {
    return <Skeleton active />;
  }

  if (currentAssessmentData.data?.overview === undefined) {
    return (
      <ErrorDisplay
        error={
          new Error(
            '[QuestionnaireControlFlowWithUser] No pending assessment data is available'
          )
        }
      />
    );
  }

  /**
   * The patient is not currently completing an assessment
   */
  if (currentAssessmentData.data?.pendingInventoryData === undefined) {
    return <Redirect to={'/'} />;
  }

  /**
   * BREAK/PAUSE LOGIC
   *
   * Triggers when the user navigated to this component from a questionnaire
   */
  if (locationState.state?.fromQuestionnaire) {
    if (locationState.state?.questionnaireName === 'promis') {
      return (
        <Redirect
          to={getPatientLinkUrl(
            PatientRoutes.CompletionPromis,
            props.patientId
          )}
        />
      );
    }

    /**
     * If the last navigation was from a non-promis questionnaire, there is one more test, and ImPACT is not done,
     * Then redirect to the completion route to give the user a break
     */
    if (
      questionnaireControlState.fulfilled + 1 ===
        questionnaireControlState.required &&
      questionnaireControlState.done['impact'] === false
    ) {
      return (
        <Redirect
          to={getPatientLinkUrl(PatientRoutes.Completion, props.patientId)}
        />
      );
    }
  }

  const inventoryData = currentAssessmentData.data ?? {};
  inventoryData.pendingInventoryData.inventoriesImpact;
  /**
   * Resolve the next questionnaire that should be loaded
   *
   * WARNING: Typecast to `any` because the generated openapi types marks inventoryImPACT.data as having an `unknown`-typed key
   */
  const ResolvedQuestionnaireIntro = resolveNeededComponent(
    inventoryData as any
  );

  /**
   * No more questionnaires are needed
   */
  if (ResolvedQuestionnaireIntro === null) {
    return (
      <>
        <Typography.Title>All tests have been completed</Typography.Title>
        <Typography>This page will reload automatically.</Typography>
      </>
    );
  }
  return (
    <ResolvedQuestionnaireIntro
      patientId={props.patientId}
      assessmentType={currentAssessmentData.data.overview?.assessmentType}
    />
  );
}
